/**
 * TutorialOverlay Component
 *
 * Displays tutorial step content with Professor Oak guiding the player.
 * Features modal overlay, step content, tips, and navigation.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Lightbulb } from 'lucide-react';
import { useTutorial } from '../contexts/TutorialContext';
import { getStepContent } from '../data/tutorialSteps';

// Professor Oak image path
const PROFESSOR_OAK_IMAGE = '/images/trainers/supports/professor_oak.png';

const TutorialOverlay = ({ stepId, onContinue, showProgress = true, showContinue = true }) => {
  const { getCurrentStepIndex, getTotalSteps } = useTutorial();

  const stepContent = getStepContent(stepId);

  if (!stepContent) {
    console.warn('[TutorialOverlay] No content for step:', stepId);
    return null;
  }

  const { title, content, tips } = stepContent;
  const currentIndex = getCurrentStepIndex();
  const totalSteps = getTotalSteps();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 50, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white rounded-2xl shadow-card-lg w-full max-w-lg overflow-hidden"
        >
          {/* Header with Professor Oak */}
          <div className="bg-gradient-to-r from-pocket-red to-red-600 p-4 flex items-center gap-4">
            {/* Professor Oak portrait */}
            <div className="w-16 h-16 rounded-full bg-white overflow-hidden flex-shrink-0 border-2 border-white shadow-md">
              <img
                src={PROFESSOR_OAK_IMAGE}
                alt="Professor Oak"
                className="w-full h-full object-cover object-top"
                style={{ transform: 'scale(1.8) translateY(15%)' }}
              />
            </div>
            <div className="flex-1">
              <h2 className="text-white font-bold text-lg">{title}</h2>
              {showProgress && (
                <div className="text-white/80 text-xs mt-1">
                  Step {currentIndex + 1} of {totalSteps}
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Main message */}
            <p className="text-pocket-text text-base leading-relaxed">
              {content}
            </p>

            {/* Tips section */}
            {tips && tips.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb size={16} className="text-amber-500" />
                  <span className="text-amber-700 font-semibold text-sm">Tips</span>
                </div>
                <ul className="space-y-1">
                  {tips.map((tip, index) => (
                    <li key={index} className="text-amber-800 text-sm flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Continue button */}
          {showContinue && (
            <div className="p-4 pt-0">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onContinue}
                className="w-full bg-pocket-red hover:bg-red-600 text-white font-bold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors"
              >
                Continue
                <ChevronRight size={20} />
              </motion.button>
            </div>
          )}

          {/* Progress bar */}
          {showProgress && (
            <div className="h-1 bg-gray-100">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${((currentIndex + 1) / totalSteps) * 100}%` }}
                transition={{ duration: 0.5 }}
                className="h-full bg-pocket-red"
              />
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Inline tutorial hint (smaller, non-blocking)
 * Used for contextual tips during gameplay
 */
export const TutorialHint = ({ stepId, onDismiss, position = 'bottom' }) => {
  const stepContent = getStepContent(stepId);

  if (!stepContent) return null;

  const { title, content } = stepContent;

  const positionClasses = {
    top: 'top-4 left-1/2 -translate-x-1/2',
    bottom: 'bottom-4 left-1/2 -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'bottom-right': 'bottom-4 right-4'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: position.includes('top') ? -20 : 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: position.includes('top') ? -20 : 20 }}
      className={`fixed ${positionClasses[position]} z-40 max-w-sm`}
    >
      <div className="bg-white rounded-xl shadow-card-lg border-2 border-pocket-red overflow-hidden">
        {/* Small Oak icon + title */}
        <div className="bg-pocket-red px-3 py-2 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white overflow-hidden flex-shrink-0">
            <img
              src={PROFESSOR_OAK_IMAGE}
              alt="Oak"
              className="w-full h-full object-cover object-top"
              style={{ transform: 'scale(1.8) translateY(15%)' }}
            />
          </div>
          <span className="text-white font-bold text-sm">{title}</span>
        </div>
        {/* Content */}
        <div className="p-3">
          <p className="text-pocket-text text-sm">{content}</p>
          <button
            onClick={onDismiss}
            className="mt-2 text-pocket-red font-semibold text-sm hover:underline"
          >
            Got it!
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default TutorialOverlay;
